import * as React from "react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Modal } from "../../../ui/components/Modal";
import { Callout } from "../../../ui/components/Callout";
import { TextInput } from "../../../ui/components/Form";
import {
  createComponent,
  updateComponent,
  fetchDataCatalogs,
} from "../../lib/api/products";
import { useI18n } from "../../lib/i18n";
import type { Component, DataCatalog } from "../../lib/api/products";

const COMPONENT_TYPES = [
  "ems", "wcm", "ems_c",
  "pcs", "inv", "inverter",
  "pdu", "battery",
  "dcdc", "pmu", "hvac",
  "ugm", "axion", "mppt", "gen",
] as const;

export interface ComponentFormModalProps {
  open: boolean;
  onClose: () => void;
  productId: string;
  accessToken: string;
  /** When provided, editing an existing component. Otherwise, creating a new one. */
  existing?: Component;
  onSaved: (component: Component) => void;
}

interface FormValues {
  componentType: string;
  iotComponentId: string;
  dataCatalogId: string;
  name: string;
  componentIndex: string;
  serial: string;
  macAddress: string;
  manufacturer: string;
  model: string;
  hwVersion: string;
  fwVersion: string;
  isActive: boolean;
}

const emptyForm = (): FormValues => ({
  componentType: "ems",
  iotComponentId: "",
  dataCatalogId: "",
  name: "",
  componentIndex: "",
  serial: "",
  macAddress: "",
  manufacturer: "",
  model: "",
  hwVersion: "",
  fwVersion: "",
  isActive: true,
});

function componentToForm(c: Component): FormValues {
  return {
    componentType: c.componentType,
    iotComponentId: c.iotComponentId ?? "",
    dataCatalogId: c.dataCatalogId ?? "",
    name: c.name ?? "",
    componentIndex: c.componentIndex != null ? String(c.componentIndex) : "",
    serial: c.serial ?? "",
    macAddress: c.macAddress ?? "",
    manufacturer: c.manufacturer ?? "",
    model: c.model ?? "",
    hwVersion: c.hwVersion ?? "",
    fwVersion: c.fwVersion ?? "",
    isActive: c.isActive,
  };
}

export function ComponentFormModal({
  open,
  onClose,
  productId,
  accessToken,
  existing,
  onSaved,
}: ComponentFormModalProps) {
  const { t, tx } = useI18n();
  const isEdit = !!existing;

  const [values, setValues] = React.useState<FormValues>(
    existing ? componentToForm(existing) : emptyForm(),
  );
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [dataCatalogs, setDataCatalogs] = React.useState<DataCatalog[]>([]);

  React.useEffect(() => {
    if (!open) return;
    setValues(existing ? componentToForm(existing) : emptyForm());
    setError(null);
    fetchDataCatalogs(accessToken)
      .then((all) => setDataCatalogs(all.filter((c) => c.isActive)))
      .catch(() => setDataCatalogs([]));
  }, [open, existing, accessToken]);

  const set = (field: keyof FormValues, value: string | boolean) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!values.componentType.trim()) {
      setError("Component type is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        componentType: values.componentType.trim(),
        iotComponentId: values.iotComponentId.trim() || undefined,
        dataCatalogId: values.dataCatalogId || undefined,
        name: values.name.trim() || undefined,
        componentIndex: values.componentIndex ? parseInt(values.componentIndex, 10) : undefined,
        serial: values.serial.trim() || undefined,
        macAddress: values.macAddress.trim() || undefined,
        manufacturer: values.manufacturer.trim() || undefined,
        model: values.model.trim() || undefined,
        hwVersion: values.hwVersion.trim() || undefined,
        fwVersion: values.fwVersion.trim() || undefined,
        isActive: values.isActive,
      };

      const saved = isEdit
        ? await updateComponent(accessToken, productId, existing!.id, payload)
        : await createComponent(accessToken, productId, { ...payload, isActive: values.isActive });

      onSaved(saved);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? "Failed to save component.");
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    padding: "8px 10px",
    borderRadius: 6,
    border: "1px solid #D1D5DB",
    fontSize: 14,
    background: "var(--color-inputBg, #fff)",
    color: "var(--color-fg, #111)",
    width: "100%",
    boxSizing: "border-box" as const,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 500,
    color: "#6B7280",
    display: "block",
    marginBottom: 4,
  };

  return (
    <Modal open={open} setOpen={(v) => { if (!v) onClose(); }}>
      <Text level="h3" weight="bold" style={{ marginBottom: 16 }}>
        {isEdit ? "Edit Component" : "Add Component"}
      </Text>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {error && <Callout variant="error">{error}</Callout>}

        {/* Component type */}
        <div>
          <label style={labelStyle}>{tx("pro.components.componentTypeRequired")}</label>
          <select
            value={values.componentType}
            onChange={(e) => set("componentType", e.target.value)}
            style={inputStyle}
            disabled={saving}
            required
          >
            {COMPONENT_TYPES.map((type) => (
              <option key={type} value={type}>{type.toUpperCase()}</option>
            ))}
          </select>
        </div>

        {/* Name + index */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12 }}>
          <TextInput
            label={t("pro.components.displayName")}
            value={values.name}
            onChange={(e) => set("name", e.target.value)}
            disabled={saving}
            placeholder={t("pro.components.placeholder.displayName")}
          />
          <div style={{ width: 80 }}>
            <TextInput
              label={t("pro.components.index")}
              value={values.componentIndex}
              onChange={(e) => set("componentIndex", e.target.value)}
              disabled={saving}
              placeholder="0"
            />
          </div>
        </div>

        {/* IoT component ID */}
        <TextInput
          label={t("pro.components.iotComponentId")}
          value={values.iotComponentId}
          onChange={(e) => set("iotComponentId", e.target.value)}
          disabled={saving}
          placeholder={t("pro.components.placeholder.iotComponentId")}
        />

        {/* Data Catalog */}
        <div>
          <label style={labelStyle}>{tx("pro.components.dataCatalog")}</label>
          <select
            value={values.dataCatalogId}
            onChange={(e) => set("dataCatalogId", e.target.value)}
            style={inputStyle}
            disabled={saving}
          >
            <option value="">— Use default for type —</option>
            {dataCatalogs.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        {/* Identifiers */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <TextInput
            label={t("pro.products.serialNumber")}
            value={values.serial}
            onChange={(e) => set("serial", e.target.value)}
            disabled={saving}
            placeholder={t("pro.components.placeholder.serial")}
          />
          <TextInput
            label={t("pro.products.macAddress")}
            value={values.macAddress}
            onChange={(e) => set("macAddress", e.target.value)}
            disabled={saving}
            placeholder="AA:BB:CC:DD:EE:FF"
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <TextInput
            label={t("pro.components.manufacturer")}
            value={values.manufacturer}
            onChange={(e) => set("manufacturer", e.target.value)}
            disabled={saving}
          />
          <TextInput
            label={t("pro.products.model")}
            value={values.model}
            onChange={(e) => set("model", e.target.value)}
            disabled={saving}
          />
        </div>

        {/* Version placeholders */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <TextInput
            label={t("pro.components.hwVersion")}
            value={values.hwVersion}
            onChange={(e) => set("hwVersion", e.target.value)}
            disabled={saving}
            placeholder="e.g. 1.0"
          />
          <TextInput
            label={t("pro.components.fwVersion")}
            value={values.fwVersion}
            onChange={(e) => set("fwVersion", e.target.value)}
            disabled={saving}
            placeholder="e.g. 2.1.4"
          />
        </div>

        {/* Active toggle */}
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14 }}>
          <input
            type="checkbox"
            checked={values.isActive}
            onChange={(e) => set("isActive", e.target.checked)}
            disabled={saving}
          />
          Active (include in telemetry polling)
        </label>

        {/* Actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 4 }}>
          <Button type="button" color="primary" variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="solid" loading={saving}>
            {isEdit ? "Save changes" : "Add component"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
